import { useEffect, useMemo, useState } from 'react';
import type { PdfSignature } from '@canvas/shared-types';
import { usePdfStore } from '@/stores/pdfStore';
import {
  deleteSignature,
  getSignatureBlob,
  listSignatures,
  resolveSignatureBlob,
} from '@/lib/pdf/signatureStore';
import { signatureRenderUrl } from '@/lib/pdf/placeSignature';

interface PdfSignaturesPanelProps {
  userId: string;
}

function SignatureThumb({ sig }: { sig: PdfSignature }) {
  const [src, setSrc] = useState(() => signatureRenderUrl(sig));

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const applyBlob = (blob: Blob) => {
      if (cancelled) return;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    };

    // Show cached bitmap immediately (common after listSignatures warms IDB)
    const mem = getSignatureBlob(sig.signatureId);
    if (mem && mem.size > 0) {
      applyBlob(mem);
    }

    void resolveSignatureBlob(sig)
      .then((blob) => applyBlob(blob))
      .catch(() => {
        if (!cancelled) {
          const fallback = signatureRenderUrl(sig);
          if (fallback) setSrc(fallback);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when identity / remote refs change
  }, [sig.signatureId, sig.downloadUrl, sig.storagePath, sig.dataUrl]);

  if (!src) {
    return <span className="pdfe-sig-thumb-missing">Re-create</span>;
  }

  return <img src={src} alt={sig.name} />;
}

export default function PdfSignaturesPanel({ userId }: PdfSignaturesPanelProps) {
  const signatures = usePdfStore((s) => s.signatures);
  const selectedSignatureId = usePdfStore((s) => s.selectedSignatureId);
  const signaturesLoaded = usePdfStore((s) => s.signaturesLoaded);
  const setSignatures = usePdfStore((s) => s.setSignatures);
  const setSelectedSignatureId = usePdfStore((s) => s.setSelectedSignatureId);
  const removeSignature = usePdfStore((s) => s.removeSignature);
  const setSignatureModalOpen = usePdfStore((s) => s.setSignatureModalOpen);
  const setError = usePdfStore((s) => s.setError);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(!signaturesLoaded);

  useEffect(() => {
    if (!userId) return;

    // Already hydrated (prefetch or prior visit) — show store immediately
    if (usePdfStore.getState().signaturesLoaded) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);

    void listSignatures(userId)
      .then((list) => {
        if (!alive) return;
        setSignatures(list);
        if (list.length && !usePdfStore.getState().selectedSignatureId) {
          setSelectedSignatureId(list[0].signatureId);
        }
      })
      .catch((err) => {
        console.warn('Signature load failed', err);
        // Don't mark loaded-empty on failure — allow retry next mount
        if (alive) setLoading(false);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [userId, setSignatures, setSelectedSignatureId]);

  // If another path finishes loading while we are mounted, drop the spinner
  useEffect(() => {
    if (signaturesLoaded) setLoading(false);
  }, [signaturesLoaded]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return signatures;
    return signatures.filter((s) => s.name.toLowerCase().includes(q));
  }, [signatures, query]);

  const handleSelect = (sig: PdfSignature) => {
    setSelectedSignatureId(sig.signatureId);
    // Warm blob cache before user clicks the page
    void resolveSignatureBlob(sig).catch(() => undefined);
  };

  const handleDelete = async (signatureId: string) => {
    const sig = signatures.find((s) => s.signatureId === signatureId);
    if (!sig) return;
    if (!confirm(`Delete signature “${sig.name}”?`)) return;
    try {
      await deleteSignature(userId, sig);
      removeSignature(signatureId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete signature');
    }
  };

  return (
    <aside className="pdfe-options pdfe-signatures-panel">
      <h2>Signatures</h2>
      <input
        className="pdfe-sig-search"
        type="search"
        placeholder="Search signatures…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search signatures"
      />
      <button
        type="button"
        className="btn btn-primary pdfe-sig-create-btn"
        onClick={() => setSignatureModalOpen(true)}
      >
        + Create signature
      </button>

      {loading && signatures.length === 0 ? (
        <p className="pdfe-options-hint">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="pdfe-options-empty pdfe-sig-empty">
          <p>
            {query
              ? 'No matching signatures.'
              : 'No signatures yet. Create one to place on your PDF.'}
          </p>
        </div>
      ) : (
        <ul className="pdfe-sig-list">
          {filtered.map((sig) => (
            <li key={sig.signatureId}>
              <button
                type="button"
                className={`pdfe-sig-card${
                  selectedSignatureId === sig.signatureId ? ' is-selected' : ''
                }`}
                onClick={() => handleSelect(sig)}
              >
                <SignatureThumb sig={sig} />
                <span>{sig.name}</span>
              </button>
              <button
                type="button"
                className="pdfe-sig-trash"
                title="Delete signature"
                aria-label={`Delete ${sig.name}`}
                onClick={() => void handleDelete(sig.signatureId)}
              >
                🗑
              </button>
            </li>
          ))}
        </ul>
      )}

      {selectedSignatureId && (
        <p className="pdfe-options-hint">
          Click the page to place. After placing, drag to move or resize (Pointer tool).
        </p>
      )}
    </aside>
  );
}
